import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Badge,
  Button,
  FilterChips,
  StatsGrid,
  ConversationPreview,
  Pagination,
  type FilterChipItem,
  type ConversationEntry,
} from '@teals/ui-web';
import {
  useGetSessionQuery,
  useLazyGetSessionQuery,
  type Session,
  type SessionDetail,
  type SessionMessage,
  type Agent,
} from '../../store/api';

/* ────────────────── Props ────────────────── */

export interface SessionsTabProps {
  agent: Agent;
  sessions: Session[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSwitchToPlayground: () => void;
  onOpenSessionInPlayground?: (sessionId: string, conversation: SessionMessage[]) => void;
}

/* ────────────────── Helpers ────────────────── */

function relativeTime(iso: string | undefined): string {
  if (!iso) return '—';
  const parsed = !/Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso + 'Z' : iso;
  const ts = new Date(parsed).getTime();
  if (Number.isNaN(ts)) return '—';
  const diff = Date.now() - ts;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/* ────────────────── Styled ────────────────── */

const TwoPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  min-height: 600px;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-primary, #fff);
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelLeft = styled(Panel)`
  border-right: 1px solid var(--border, #e2e8f0);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border-bottom: 1px solid var(--border, #e2e8f0);
  color: var(--text-primary, #0f172a);
  background: var(--bg-elevated, #f8fafc);
`;

const PanelScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SessionItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-radius: 8px;
  border-left: 3px solid ${({ $selected }) => ($selected ? 'var(--accent, #1a7a70)' : 'transparent')};
  background: ${({ $selected }) => ($selected ? 'var(--bg-elevated, #f8fafc)' : 'transparent')};
  transition: background 0.12s ease;

  &:hover {
    background: var(--bg-elevated, #f8fafc);
  }
`;

const StatusDot = styled.div<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
  background: ${({ $status }) =>
    $status === 'success' ? '#16a34a' : $status === 'failed' ? '#dc2626' : '#2563eb'};
`;

const SessionBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const Summary = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary, #0f172a);
  line-height: 1.4;
  margin-bottom: 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.6875rem;
  color: var(--text-muted, #94a3b8);
`;

const MonoId = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--text-secondary, #64748b);
`;

const PaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;
`;

/* Right panel */
const DetailEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 0.75rem;
  color: var(--text-muted, #94a3b8);
  font-size: 0.8125rem;
`;

const DetailIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--bg-elevated, #f1f5f9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const DetailContent = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const DetailHeaderText = styled.div`
  flex: 1;
`;

const DetailTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary, #0f172a);
`;

const DetailSub = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted, #94a3b8);
  margin-top: 0.125rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const ConvoSection = styled.div`
  margin-top: 0.25rem;
`;

const ConvoTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  margin-bottom: 0.75rem;
`;

const HeaderRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

/* ────────────────── Component ────────────────── */

export const SessionsTab: React.FC<SessionsTabProps> = ({
  agent,
  sessions,
  page,
  totalPages,
  onPageChange,
  onSwitchToPlayground,
  onOpenSessionInPlayground,
}) => {
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(
    sessions[0]?.sessionId ?? null,
  );

  const { data: sessionDetail } = useGetSessionQuery(selectedId!, {
    skip: !selectedId,
  });
  const [triggerGetSession] = useLazyGetSessionQuery();

  const filtered = useMemo(() => {
    if (filter === 'all') return sessions;
    if (filter === 'success') return sessions.filter((s) => s.status === 'success');
    if (filter === 'failed') return sessions.filter((s) => s.status === 'failed');
    return sessions;
  }, [sessions, filter]);

  const successCount = sessions.filter((s) => s.status === 'success').length;
  const failedCount = sessions.filter((s) => s.status === 'failed').length;

  const filterItems: FilterChipItem[] = [
    { id: 'all', label: 'All', count: sessions.length },
    { id: 'success', label: 'Successful', count: successCount },
    { id: 'failed', label: 'Failed', count: failedCount },
  ];

  const handleFilterChange = (id: string) => {
    setFilter(id);
  };

  const selected = sessions.find((s) => s.sessionId === selectedId);

  const handleOpenSession = (sessionId: string) => {
    if (!onOpenSessionInPlayground) return;
    const cached = sessionId === selectedId ? (sessionDetail as SessionDetail | undefined) : undefined;
    if (cached?.conversation) {
      onOpenSessionInPlayground(sessionId, cached.conversation);
      return;
    }
    triggerGetSession(sessionId)
      .unwrap()
      .then((res) => onOpenSessionInPlayground(sessionId, res.conversation ?? []))
      .catch(() => {});
  };

  const conversationEntries: ConversationEntry[] = useMemo(() => {
    if (!sessionDetail) return [];
    const detail = sessionDetail as SessionDetail;
    if (!detail.conversation) return [];
    return detail.conversation.map((m) => ({
      type: 'message' as const,
      data: {
        role: m.role === 'user' ? 'User' : m.role === 'assistant' ? (agent.displayName ?? agent.agentName) : 'System',
        content: m.content,
        roleColor: m.role === 'user' ? '#2563eb' : m.role === 'assistant' ? '#1a7a70' : '#94a3b8',
      },
    }));
  }, [sessionDetail, agent.displayName, agent.agentName]);

  return (
    <TwoPanel>
      {/* ──── Left: Session List ──── */}
      <PanelLeft>
        <PanelHeader>
          &#x23F1; Sessions
          <HeaderRight>
            <Button variant="primary" size="sm" onClick={onSwitchToPlayground}>
              &#x2795; New
            </Button>
          </HeaderRight>
        </PanelHeader>
        <PanelScroll>
          <Toolbar>
            <FilterChips items={filterItems} activeId={filter} onChange={handleFilterChange} />
          </Toolbar>

          {filtered.length === 0 ? (
            <DetailEmpty style={{ minHeight: 200 }}>
              <DetailIcon>&#x1F4AD;</DetailIcon>
              No sessions match this filter.
            </DetailEmpty>
          ) : (
            <ListWrap>
              {filtered.map((s) => (
                <SessionItem
                  key={s.sessionId}
                  $selected={s.sessionId === selectedId}
                  onClick={() => setSelectedId(s.sessionId)}
                >
                  <StatusDot $status={s.status} />
                  <SessionBody>
                    <Summary>{s.summary || `Session #${s.sessionId.slice(0, 8)}`}</Summary>
                    <Meta>
                      <MonoId>#{s.sessionId.slice(0, 6)}</MonoId>
                      <span>{formatDuration(s.totalDurationMs)}</span>
                      <span>{s.messageCount} messages</span>
                      {s.delegationCount > 0 && <span>{s.delegationCount} delegations</span>}
                      <span>{relativeTime(s.updatedAt ?? s.createdAt)}</span>
                    </Meta>
                  </SessionBody>
                  <Badge
                    variant={s.status === 'success' ? 'success' : s.status === 'failed' ? 'error' : 'info'}
                    size="sm"
                  >
                    {s.status === 'success' ? 'Success' : s.status === 'failed' ? 'Failed' : 'Running'}
                  </Badge>
                </SessionItem>
              ))}
            </ListWrap>
          )}

          {totalPages > 1 && (
            <PaginationWrap>
              <Pagination currentPage={page} totalPages={totalPages} onChange={onPageChange} />
            </PaginationWrap>
          )}
        </PanelScroll>
      </PanelLeft>

      {/* ──── Right: Session Detail Preview ──── */}
      <Panel>
        <PanelHeader>
          &#x1F50D; Session Detail
          {selected && (
            <HeaderRight>
              <Button variant="ghost" size="sm" onClick={() => handleOpenSession(selected.sessionId)}>
                Open Full &#x2192;
              </Button>
            </HeaderRight>
          )}
        </PanelHeader>
        {!selected ? (
          <DetailEmpty>
            <DetailIcon>&#x1F50D;</DetailIcon>
            Select a session to view details
          </DetailEmpty>
        ) : (
          <PanelScroll>
            <DetailContent>
              <DetailHeader>
                <StatusDot $status={selected.status} style={{ width: 12, height: 12, marginTop: 0 }} />
                <DetailHeaderText>
                  <DetailTitle>{selected.summary || `Session #${selected.sessionId.slice(0, 8)}`}</DetailTitle>
                  <DetailSub>
                    <MonoId>#{selected.sessionId.slice(0, 6)}</MonoId>
                    &middot;
                    {relativeTime(selected.updatedAt ?? selected.createdAt)}
                  </DetailSub>
                </DetailHeaderText>
                <Badge
                  variant={selected.status === 'success' ? 'success' : selected.status === 'failed' ? 'error' : 'info'}
                  size="sm"
                >
                  {selected.status === 'success' ? 'Success' : selected.status === 'failed' ? 'Failed' : 'Running'}
                </Badge>
              </DetailHeader>

              {sessionDetail && ((sessionDetail as SessionDetail).userName || (sessionDetail as SessionDetail).userEmail || (sessionDetail as SessionDetail).organizationName) && (
                <StatsGrid
                  items={[
                    ...((sessionDetail as SessionDetail).userName ? [{ label: 'User', value: (sessionDetail as SessionDetail).userName! }] : []),
                    ...((sessionDetail as SessionDetail).userEmail ? [{ label: 'Email', value: (sessionDetail as SessionDetail).userEmail! }] : []),
                    ...((sessionDetail as SessionDetail).organizationName ? [{ label: 'Organization', value: (sessionDetail as SessionDetail).organizationName! }] : []),
                  ]}
                  columns={2}
                />
              )}
              <StatsGrid
                items={[
                  {
                    label: 'Response Time',
                    value: formatDuration(
                      (sessionDetail as SessionDetail)?.totalDurationMs ?? selected.totalDurationMs,
                    ),
                  },
                  {
                    label: 'Messages',
                    value: (sessionDetail as SessionDetail)?.messageCount ?? selected.messageCount,
                  },
                  {
                    label: 'Delegations',
                    value: (sessionDetail as SessionDetail)?.delegationCount ?? selected.delegationCount,
                  },
                  {
                    label: 'Tokens',
                    value:
                      ((sessionDetail as SessionDetail)?.messageCount ?? selected.messageCount) * 36,
                  },
                ]}
                columns={2}
              />

              <ConvoSection>
                <ConvoTitle>&#x1F4AC; Conversation Preview</ConvoTitle>
                {conversationEntries.length > 0 ? (
                  <ConversationPreview entries={conversationEntries} />
                ) : (
                  <Meta style={{ justifyContent: 'center', padding: '1.5rem 0' }}>
                    No conversation data available
                  </Meta>
                )}
              </ConvoSection>
            </DetailContent>
          </PanelScroll>
        )}
      </Panel>
    </TwoPanel>
  );
};

export default SessionsTab;
